<template>
  <LogDialog
    v-model="openLogs"
    :entity-type="'episode'"
    :entity-id="episode.id"
    :title="`${episode.title?.text || ''} ${episode.prompt_topic ? `(${episode.prompt_topic})` : ''}`"
  />
  <v-dialog
    v-model="openDialog"
    max-width="700px"
    min-height="600px"
    scrollable
  >
    <v-card class="px-4">
      <v-btn
        v-if="!isGuest"
        class="mt-4"
        variant="tonal"
        @click="openLogs = true"
      >
        <v-icon>
          mdi-robot-happy
        </v-icon>
        Show episode creationg logs
      </v-btn>
      <div class="mt-4 mx-8">
        {{ episode.title?.text }} {{ episode.prompt_topic ? `(${episode.prompt_topic})` : '' }}
      </div>
      <v-divider class="mt-2" />
      <v-table>
        <tbody>
          <tr
            v-for="(line, idx2) in episode.script || []"
            :key="idx2"
          >
            <td
              v-if="line.type === 'dialog'"
              style="width:70px;"
              class="pa-1"
            >
              <div
                v-if="!findCharacter(line.data.actor || '') && line.data.generatedCharacter"
                class="character-sprite-stack"
              >
                <template
                  v-for="(layers, idx3) in generatedCharacterLayerStyles(line)"
                  :key="idx3"
                >
                  <div
                    class="character-sprite character-sprite-layer"
                    :style="layers.shadow"
                  />
                  <div
                    class="character-sprite character-sprite-layer"
                    :style="layers.plain"
                  />
                </template>
              </div>
              <div
                v-else
                class="character-sprite"
                :style="characterSpriteStyle(line)"
              />
            </td>
            <td
              v-if="line.type === 'dialog'"
              class="pa-1"
            >
              <div>{{ line.data.actor }}</div>
              <div class="text-caption text-grey mb-3">
                {{ line.data.emotion }}
              </div>
              <div>
                {{ line.data.text }}
              </div>
            </td>
            <td
              v-if="line.type === 'narration'"
              colspan="2"
              class="pa-1"
            >
              <div><em>{{ line.data.text }}</em></div>
            </td>
            <td
              v-if="line.type === 'location'"
              colspan="2"
              class="pa-1"
            >
              <div>LOCATION: <code>{{ line.data.location }} ({{ line.data.sceneId }})</code></div>
              <div>
                <span>📌 {{ scene(line.data.sceneId).name }}</span>
                <span v-if="idx2 === 0">
                  👘 {{ scene(line.data.sceneId).clothingStyle }}
                </span>
              </div>
              <img
                :src="`/assets/scenes-5/${scene(line.data.sceneId).imageNiji5}`"
                width="320"
              >
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </v-dialog>
  <v-card
    :image="`/assets/scenes-5/${beginningScene.imageNiji5}`"
    class="control-episode-item"
    elevation="2"
    :loading="episode.status === EpisodeStatus.PROCESSING"
  >
    <div class="d-flex flex-column mx-4 mt-2">
      <div class="speech-bubbles-container">
        <div
          v-if="episode.prompt_username"
          class="speech-bubble left"
        >
          <div
            v-if="props.episode.prompt_type === PromptType.TWITCH_REDEMPTION || props.episode.prompt_type === PromptType.TWITCH_COMMAND"
            class="ml-2 font-weight-bold text-subtitle-1 text-medium-emphasis"
          >
            <span
              :class="twitchColor"
            >@{{ episode.prompt_username }}</span> redeemed ✨Episode Topic✨
          </div>
          <div
            v-if="props.episode.prompt_type === PromptType.TWITCH_SEQUEL_COMMAND || props.episode.prompt_type === PromptType.TWITCH_SEQUEL_REDEMPTION"
            class="ml-2 font-weight-bold text-subtitle-1 text-medium-emphasis"
          >
            <span
              :class="twitchColor"
            >@{{ episode.prompt_username }}</span> redeemed ⏩Sequel Episode⏩
          </div>
          <div
            v-else-if="props.episode.prompt_type === PromptType.TWITCH_POLL"
            class="ml-2 font-weight-bold text-subtitle-1 text-medium-emphasis"
          >
            Twitch poll:
          </div>
          <v-divider />
          <div
            v-if="episode.prompt_topic"
            class="ml-2 mt-2 text-body-2 text-medium-emphasis"
          >
            {{ episode.prompt_topic }}
          </div>
          <v-btn
            v-if="props.episode.prompt_topic"
            class="hover-copy-button"
            size="x-small"
            icon="mdi-clipboard-file-outline"
            @click="copyPromptToClipboard()"
          />
        </div>
        <div
          v-if="props.episode.status === 'aborted'"
          class="speech-bubble right"
        >
          <div class="font-weight-bold text-overline text-warning">
            ✋ The episode was cancelled by the admins.
          </div>
        </div>
        <div
          v-else-if="props.episode.status === 'error'"
          class="speech-bubble right"
        >
          <div class="font-weight-bold text-overline text-error">
            🤖 An error occured, no episode was generated.
          </div>
        </div>
        <div
          v-else-if="props.episode.status === 'flagged'"
          class="speech-bubble right"
        >
          <div class="font-weight-bold text-overline text-error">
            😳 No appropriate script could be generated.
          </div>
        </div>
      </div>
      <span class="episode-title d-flex flex-wrap align-center">
        <img
          v-for="(ch, idx) in characters"
          :key="idx"
          :src="`/assets/character-icons/${ch.id}.png`"
          class="episode-character-icon"
        > <span class="episode-title-text"> {{ episode.title?.text }}</span>
      </span>
    </div>
    <v-card-text class="flat-color">
      <div class="episode-info">
        <v-chip
          v-if="canViewEpisodeScript"
          size="small"
          class="mr-2"
          prepend-icon="mdi-nature-people"
          color="primary"
          @click="openDialog = true"
        >
          <span>Show script</span>
        </v-chip>
        <v-chip
          size="small"
          class="mr-2"
          prepend-icon="mdi-clipboard-file-outline"
          @click="copyIdToClipboard()"
        >
          <span>{{ `ID: ${episode.id}` }}</span>
        </v-chip>
        <v-chip
          v-if="!canCopyEpisodeScript"
          size="x-small"
          class="mr-2"
        >
          <span>{{ episode.script?.length || 0 }} lines of script</span>
        </v-chip>
        <v-chip
          v-if="canCopyEpisodeScript"
          size="x-small"
          class="mr-2"
          prepend-icon="mdi-clipboard-file-outline"
          @click="copyScriptToClipboard()"
        >
          <span>{{ episode.script?.length || 0 }} lines of script</span>
        </v-chip>
        <v-chip
          v-if="episode.settings_used"
          size="x-small"
          class="mr-2"
          prepend-icon="mdi-cog"
        >
          <span>{{ episode.settings_used.ai_type }} / {{ episode.settings_used.model }} / temp {{ episode.settings_used.temperature }}</span>
        </v-chip>
        <v-chip
          v-if="episode.last_played"
          size="x-small"
          class="mr-2"
          variant="text"
        >
          <span>{{ `played at ${lastPlayedDate}` }}</span>
        </v-chip>
        <v-chip
          v-else
          size="x-small"
          class="mr-2"
          disabled
          variant="text"
        >
          not played yet...
        </v-chip>
      </div>
      <v-divider
        v-if="!isGuest"
        class="my-2"
      />
      <div
        v-if="!isGuest && episode.status === EpisodeStatus.PROCESSED"
        class=" d-flex flex-row justify-center align-center"
      >
        <v-btn
          variant="outlined"
          color="blue-grey-lighten-2"
          density="compact"
          class="ml-3"
          @click="addToPlaylist"
        >
          Add to Playlist
        </v-btn>
        <v-btn
          variant="outlined"
          color="blue-grey-lighten-2"
          density="compact"
          class="ml-3"
          @click="setPlayed"
        >
          +1 Play Count
        </v-btn>
      </div>
      <div
        v-if="displayedProgress"
        class="episode-progress mb-2"
      >
        <div class="episode-progress-steps">
          <template
            v-for="(step, idx) in EPISODE_PROGRESS_STEP_ORDER"
            :key="step"
          >
            <div class="episode-progress-step-wrap">
              <div
                class="episode-progress-step"
                :class="{
                  'episode-progress-step-done': isStepDone(idx),
                  'episode-progress-step-active': isStepActive(idx),
                }"
              >
                <v-icon
                  :icon="EPISODE_PROGRESS_STEP_ICONS[step]"
                  size="16"
                />
              </div>
              <div
                class="episode-progress-step-caption"
                :class="{
                  'episode-progress-step-caption-active': isStepActive(idx),
                }"
              >
                {{ EPISODE_PROGRESS_STEP_SHORT_LABELS[step] }}
              </div>
            </div>
            <div
              v-if="idx < EPISODE_PROGRESS_STEP_ORDER.length - 1"
              class="episode-progress-connector"
              :class="{ 'episode-progress-connector-done': isStepDone(idx) }"
            />
          </template>
        </div>
        <div class="d-flex justify-space-between text-caption text-medium-emphasis mt-1">
          <span>{{ progressStepLabel }}{{ progressCounterText }}</span>
          <span v-if="progressPercent !== null">{{ progressPercent }}%</span>
        </div>
      </div>
      <v-btn
        v-if="!isGuest && episode.status === EpisodeStatus.PROCESSING"
        color="red"
        block
        @click="cancelJob"
      >
        Cancel job
      </v-btn>
    </v-card-text>
  </v-card>
</template>

<script
  setup
  lang="ts"
>
import { computed, onUnmounted, ref, watch } from 'vue'
import { getSceneById } from '../../../../common/src/Scenes'
import { DialogScriptLine, SceneDef } from '../../../../common/src/Types'
import { CharacterDef, CHARACTERS, anonHueShadowLayerStyle, anonLayerStyle, buildAnonImageStack, episodeCharacters, findCharacter, findSprite, hashHue } from '../../../../common/src/Characters'
import { EPISODE_PROGRESS_STEP_ICONS, EPISODE_PROGRESS_STEP_LABELS, EPISODE_PROGRESS_STEP_ORDER, EPISODE_PROGRESS_STEP_SHORT_LABELS, EpisodeProgress, episodeProgressPercent, episodeProgressStepNumber, EpisodesRow, EpisodeStatus } from '../../../../common/src/EpisodesRow'
import { PromptType } from '../../../../common/src/PromptDef'
import LogDialog from './LogDialog.vue'
import user from '../../user'
import { format } from 'date-fns'
import { toast } from '../../toast'
import { twitchColorToId } from '../../../../common/src/TwitchColors'
import { handleCopyingToClipboard } from '../../util'

const isGuest = computed(() => !user.getMe())

const openLogs = ref<boolean>(false)
const openDialog = ref<boolean>(false)

const props = defineProps<{
  episode: EpisodesRow,
}>()

const emit = defineEmits<{
  (e: 'add-to-playlist', val: EpisodesRow): void
  (e: 'set-played', val: EpisodesRow): void
  (e: 'cancel-job', val: EpisodesRow): void
}>()

const addToPlaylist = async () => {
  emit('add-to-playlist', props.episode)
}

const setPlayed = async () => {
  emit('set-played', props.episode)
}

const cancelJob = async () => {
  emit('cancel-job', props.episode)
}

// how long (ms) each step is guaranteed to stay visible before advancing to
// the next one - this is purely a frontend smoothing effect: the backend may
// jump through steps faster than this, but we don't want the UI to visually
// skip steps, so incoming updates for a *new* step are queued and drained
// one at a time, at least this many ms apart. Once the episode is actually
// done, the whole element is hidden right away (no extra hold) so it
// disappears in sync with the "Add to Playlist" / "+1 Play Count" buttons
// that appear at the same time.
const MIN_STEP_DISPLAY_MS = 1200

type ProgressQueueEntry =
  | { kind: 'progress', progress: EpisodeProgress }
  | { kind: 'complete' }

const displayedProgress = ref<EpisodeProgress | null>(props.episode.progress)
let stepQueue: ProgressQueueEntry[] = []
// if we mount mid-generation, treat "now" as the start of the currently
// displayed step so the very first observed transition still waits its turn
let lastStepChangeAt = props.episode.progress ? Date.now() : 0
let advanceTimer: ReturnType<typeof setTimeout> | null = null

const clearAdvanceTimer = () => {
  if (advanceTimer !== null) {
    clearTimeout(advanceTimer)
    advanceTimer = null
  }
}

const advanceQueue = () => {
  const next = stepQueue.shift()
  if (!next) {
    advanceTimer = null
    return
  }
  if (next.kind === 'complete') {
    // done - hide immediately, no extra hold
    displayedProgress.value = null
    advanceTimer = null
    return
  }
  displayedProgress.value = next.progress
  lastStepChangeAt = Date.now()
  advanceTimer = setTimeout(advanceQueue, MIN_STEP_DISPLAY_MS)
}

const lastKnownStep = (): EpisodeProgress | null => {
  for (let i = stepQueue.length - 1; i >= 0; i--) {
    const entry = stepQueue[i]
    if (entry.kind === 'progress') {
      return entry.progress
    }
  }
  return displayedProgress.value
}

watch(() => props.episode.progress, (newProgress) => {
  // episode left the processing state (done/errored/aborted/etc.)
  if (!newProgress) {
    if (!displayedProgress.value && stepQueue.length === 0) {
      // nothing was ever shown - nothing to hold, just make sure it's reset
      clearAdvanceTimer()
      displayedProgress.value = null
      return
    }
    // queue the completion so it still respects the minimum display time of
    // whatever is currently showing/queued, instead of vanishing instantly
    // mid-step, but hides right away once its turn comes up
    stepQueue.push({ kind: 'complete' })
    if (advanceTimer === null) {
      const elapsed = Date.now() - lastStepChangeAt
      const wait = Math.max(MIN_STEP_DISPLAY_MS - elapsed, 0)
      advanceTimer = setTimeout(advanceQueue, wait)
    }
    return
  }

  const current = displayedProgress.value
  if (!current) {
    // first progress update we've seen - show it right away
    displayedProgress.value = newProgress
    lastStepChangeAt = Date.now()
    return
  }

  const lastKnown = lastKnownStep() || current

  if (newProgress.step === lastKnown.step && stepQueue.length === 0) {
    // same step as what's displayed and nothing queued yet - update the
    // current/total counter live, no need to delay this
    displayedProgress.value = newProgress
    return
  }

  if (newProgress.step === lastKnown.step && stepQueue[stepQueue.length - 1]?.kind === 'progress') {
    // same step as the last queued (not-yet-shown) entry - just refresh its
    // current/total, no need for a duplicate queue entry
    stepQueue[stepQueue.length - 1] = { kind: 'progress', progress: newProgress }
  } else {
    // the backend may skip through several steps between two polls (e.g.
    // determining-scene/title have no sub-progress and can finish within a
    // single poll interval) - synthesize the skipped steps so every step
    // still gets its moment on screen instead of being skipped entirely
    const lastIdx = EPISODE_PROGRESS_STEP_ORDER.indexOf(lastKnown.step)
    const newIdx = EPISODE_PROGRESS_STEP_ORDER.indexOf(newProgress.step)
    if (lastIdx !== -1 && newIdx !== -1) {
      for (let idx = lastIdx + 1; idx < newIdx; idx++) {
        stepQueue.push({ kind: 'progress', progress: { step: EPISODE_PROGRESS_STEP_ORDER[idx], current: null, total: null } })
      }
    }
    stepQueue.push({ kind: 'progress', progress: newProgress })
  }

  if (advanceTimer === null) {
    const elapsed = Date.now() - lastStepChangeAt
    const wait = Math.max(MIN_STEP_DISPLAY_MS - elapsed, 0)
    advanceTimer = setTimeout(advanceQueue, wait)
  }
}, { deep: true })

onUnmounted(() => {
  clearAdvanceTimer()
})

const progressStepLabel = computed(() => {
  const progress = displayedProgress.value
  if (!progress) {
    return ''
  }
  return EPISODE_PROGRESS_STEP_LABELS[progress.step] || progress.step
})

const progressCounterText = computed(() => {
  const progress = displayedProgress.value
  if (!progress || progress.current === null || progress.total === null) {
    return ''
  }
  return ` (${progress.current}/${progress.total})`
})

// cap the shown percentage below 100% while still displaying a step, so the
// bar never claims completion before the job is actually done - the element
// is hidden entirely once generation truly finishes (see advanceQueue above)
const progressPercent = computed(() => {
  const percent = episodeProgressPercent(displayedProgress.value)
  if (percent === null) {
    return null
  }
  return Math.min(percent, 99)
})

// idx is 0-based index into EPISODE_PROGRESS_STEP_ORDER
const isStepDone = (idx: number): boolean => {
  const stepNumber = episodeProgressStepNumber(displayedProgress.value)
  return stepNumber !== null && idx < stepNumber - 1
}

const isStepActive = (idx: number): boolean => {
  const stepNumber = episodeProgressStepNumber(displayedProgress.value)
  return stepNumber !== null && idx === stepNumber - 1
}


const copyIdToClipboard = async () => {
  handleCopyingToClipboard(` --id ${props.episode.id}`)
  toast('Copied to clipboard: --id ' + props.episode.id, 'success')
}

const copyScriptToClipboard = async () => {
  if (!props.episode.gpt_output) {
    return
  }
  handleCopyingToClipboard(props.episode.gpt_output.toString())
  toast('Copied script of episode ' + props.episode.id, 'success')
}

const copyPromptToClipboard = async () => {
  if (!props.episode.prompt_topic) {
    return
  }
  handleCopyingToClipboard(props.episode.prompt_topic)
  toast('Copied prompt to clipboard', 'success')
}

const lastPlayedDate = computed(() => {
  if (!props.episode.last_played) {
    return ''
  }
  return format(new Date(props.episode.last_played), 'HH:mm dd MMM yyyy')
})


const canViewEpisodeScript = computed(() => {
  if (!isGuest.value) {
    return true
  }
  if (props.episode.last_played) {
    return true
  }
  return false
})

const canCopyEpisodeScript = computed(() => {
  return canViewEpisodeScript.value  && props.episode.gpt_output
})

const characterSpriteStyle = computed(() => (line: DialogScriptLine) => {
  const templateIcon = {
    backgroundImage: ``,
  }
  const ch = findCharacter(line.data.actor || '')
  if (!ch) {
    return templateIcon
  }
  if (isGuest.value) {
    return {
      backgroundImage: `url(/assets/character-icons/${ch.id}.png)`,
    }

  }
  const episodeCharacterIds = props.episode.character_ids || CHARACTERS.map(ch => ch.id)
  if (!episodeCharacterIds.includes(ch.id)) {
  return templateIcon
  }
  const sprite = findSprite(ch, line.data.emotion, beginningScene.value)
  if (sprite) {
    return {
      backgroundImage: `url(/assets/sprites/${sprite.image.split('/').map(encodeURIComponent).join('/')})`,
    }
  }
  return templateIcon
})

const generatedCharacterLayerStyles = computed(() => (line: DialogScriptLine) => {
  if (!line.data.generatedCharacter) {
    return []
  }
  const images = buildAnonImageStack(line.data.generatedCharacter, line.data.emotion || 'neutral')
  const hue = hashHue((line.data.actor || '').toLowerCase().trim())
  return images.map(img => {
    const url = `/assets/${img.split('/').map(encodeURIComponent).join('/')}`
    return {
      shadow: anonHueShadowLayerStyle(url, hue, '150%', '50% 20%'),
      plain: anonLayerStyle(url, hue, '150%', '50% 20%'),
    }
  })
})

const twitchColor = computed(() => {
  return `twitch-color-${twitchColorToId(props.episode.prompt_usercolor)}`
})

const scene = (sceneId: any): SceneDef => getSceneById(sceneId)

const beginningScene = computed((): SceneDef => getSceneById(props.episode.scene_id))

const characters = computed((): CharacterDef[] => episodeCharacters(props.episode))

</script>
